/*
__/\\\\\\\\\\\\\\\__/\\\\\\\\\\\\\\\_____/\\\\\\\\\____        
 _\///////\\\/////__\///////\\\/////____/\\\\\\\\\\\\\__       
  _______\/\\\_____________\/\\\________/\\\/////////\\\_      
   _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_     
    _______\/\\\_____________\/\\\_______\/\\\\\\\\\\\\\\\_    
     _______\/\\\_____________\/\\\_______\/\\\/////////\\\_   
      _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_  
       _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_ 
        _______\///______________\///________\///________\///__
            
            COPYRIGHT TACTICAL TRANSPORTATION ADVISORS, INC. 
            ALL RIGHTS RESERVED.
*/

import { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Card, Col, Container, Modal, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { validateDecimal } from "../../../tools";
import { getPaychexWorkerDocumentRequestData, submitPaychexWorkerDocumentRequestData } from "../../../services/DashboardService";
import CustomControl from "../../../components/CustomControl";
import SwitchControl from "../../../components/SwitchControl";
import AddressControl from "../../../components/AddressControl";
import RadioControl from "../../../components/RadioControl";
import DropdownControl from "../../../components/DropdownControl";
import CustomButton from "../../../components/CustomButton";
import CustomSpinner from "../../../components/CustomSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { AppGlobals } from "../../../App";
import { Document, Page } from "react-pdf";
import { pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export const StateTaxFilingStatus = Object.freeze({
  _2_WAGE_EARNER: "_2_WAGE_EARNER",

  A: "A",
  B: "B",
  C: "C",
  CHART_A: "CHART_A",
  CHART_B: "CHART_B",
  CHART_C: "CHART_C",
  CHART_D: "CHART_D",
  CHART_E: "CHART_E",

  CIVIL_UNION: "CIVIL_UNION",
  CIVIL_UNION__WITHHOLD_HIGHER_SINGLE_RT: "CIVIL_UNION__WITHHOLD_HIGHER_SINGLE_RT",

  CLAIMING_NO_PERSONAL_ALLOWANCES: "CLAIMING_NO_PERSONAL_ALLOWANCES",
  CLAIMING_SELF: "CLAIMING_SELF",

  D: "D",
  E: "E",
  F: "F",

  HEAD_OF_HOUSEHOLD: "HEAD_OF_HOUSEHOLD",
  HEAD_OF_HOUSEHOLD__HIGHER_RATES: "HEAD_OF_HOUSEHOLD__HIGHER_RATES",
  HEAD_OF_HOUSEHOLD_COMPLETE: "HEAD_OF_HOUSEHOLD_COMPLETE",
  HEAD_OF_HOUSEHOLD_NONE: "HEAD_OF_HOUSEHOLD_NONE",

  INDIVIDUAL_TAXPAYER_COMPLETE: "INDIVIDUAL_TAXPAYER_COMPLETE",
  INDIVIDUAL_TAXPAYER_NONE: "INDIVIDUAL_TAXPAYER_NONE",

  MARRIED: "MARRIED",
  MARRIED__SURV_SPOUSE__HOH: "MARRIED__SURV_SPOUSE__HOH",

  MARRIED_CIVIL_UNION_FILING_JOINTLY: "MARRIED_CIVIL_UNION_FILING_JOINTLY",
  MARRIED_CIVIL_UNION_FILING_SEPARATELY: "MARRIED_CIVIL_UNION_FILING_SEPARATELY",

  MARRIED_CLAIMING_BOTH: "MARRIED_CLAIMING_BOTH",
  MARRIED_CLAIMING_GREATER_OR_EQUAL_TO_2: "MARRIED_CLAIMING_GREATER_OR_EQUAL_TO_2",
  MARRIED_CLAIMING_LESS_THAN_2: "MARRIED_CLAIMING_LESS_THAN_2",

  MARRIED_COMPLETE: "MARRIED_COMPLETE",

  MARRIED_FILING_JOINTLY: "MARRIED_FILING_JOINTLY",
  MARRIED_FILING_JOINTLY__HIGHER_RATES: "MARRIED_FILING_JOINTLY__HIGHER_RATES",
  MARRIED_FILING_SEPARATE_ON_SAME_RETURN: "MARRIED_FILING_SEPARATE_ON_SAME_RETURN",
  MARRIED_FILING_SEPARATELY: "MARRIED_FILING_SEPARATELY",

  MARRIED_FLNG_JOINT_CLAIM_HALF_EXEMPTS: "MARRIED_FLNG_JOINT_CLAIM_HALF_EXEMPTS",
  MARRIED_LVNG_W_SPOUSE_FLNG_SEP_CLAIM_ALL: "MARRIED_LVNG_W_SPOUSE_FLNG_SEP_CLAIM_ALL",

  MARRIED_NONE_OR_MARRIED_OPT_COMP_NONE: "MARRIED_NONE_OR_MARRIED_OPT_COMP_NONE",
  MARRIED_NOT_LIVING_WITH_SPOUSE: "MARRIED_NOT_LIVING_WITH_SPOUSE",
  MARRIED_OPTIONAL_COMPUTATION_COMPLETE: "MARRIED_OPTIONAL_COMPUTATION_COMPLETE",
  MARRIED_PERSON_HALF: "MARRIED_PERSON_HALF",

  MARRIED_SPOUSE_NOT_WORKING: "MARRIED_SPOUSE_NOT_WORKING",

  MARRIED_WITH_MULTIPLE_EMPLOYERS: "MARRIED_WITH_MULTIPLE_EMPLOYERS",
  MARRIED_WITH_ONE_INCOME: "MARRIED_WITH_ONE_INCOME",
  MARRIED_WITH_SPOUSE_FILING: "MARRIED_WITH_SPOUSE_FILING",
  MARRIED_WITH_SPOUSE_WORKING: "MARRIED_WITH_SPOUSE_WORKING",
  MARRIED_WITH_TWO_INCOMES: "MARRIED_WITH_TWO_INCOMES",
  MARRIED_WITHHOLD_SINGLE: "MARRIED_WITHHOLD_SINGLE",
  MARRIED_WITHOUT_SPOUSE_FILING: "MARRIED_WITHOUT_SPOUSE_FILING",

  MFS_OR_MFJ__BOTH_SPOUSES_WORKING: "MFS_OR_MFJ__BOTH_SPOUSES_WORKING",
  MJQH: "MJQH",

  NO_DEDUCTION: "NO_DEDUCTION",
  OTHER: "OTHER",

  QUALIFYING_WIDOWER: "QUALIFYING_WIDOWER",
  REGULAR: "REGULAR",

  SINGLE: "SINGLE",
  SINGLE_MARRIED_CIVIL_UNION_SEP: "SINGLE_MARRIED_CIVIL_UNION_SEP",
  SINGLE_MARRIED_FILING_SEP__HIGHER_RATES: "SINGLE_MARRIED_FILING_SEP__HIGHER_RATES",
  SINGLE_MARRIED_WITH_TWO_OR_MORE_INCOMES: "SINGLE_MARRIED_WITH_TWO_OR_MORE_INCOMES",
  SINGLE_OR_MARRIED_FILING_SEPARATELY: "SINGLE_OR_MARRIED_FILING_SEPARATELY",
});

export const StateTaxFilingStatusLabels = {
  [StateTaxFilingStatus._2_WAGE_EARNER]: "Two Wage Earners",

  [StateTaxFilingStatus.A]: "Chart A",
  [StateTaxFilingStatus.B]: "Chart B",
  [StateTaxFilingStatus.C]: "Chart C",
  [StateTaxFilingStatus.CHART_A]: "Chart A",
  [StateTaxFilingStatus.CHART_B]: "Chart B",
  [StateTaxFilingStatus.CHART_C]: "Chart C",
  [StateTaxFilingStatus.CHART_D]: "Chart D",
  [StateTaxFilingStatus.CHART_E]: "Chart E",

  [StateTaxFilingStatus.CIVIL_UNION]: "Civil Union",
  [StateTaxFilingStatus.CIVIL_UNION__WITHHOLD_HIGHER_SINGLE_RT]: "Civil Union – Withhold at Higher Single Rate",

  [StateTaxFilingStatus.CLAIMING_NO_PERSONAL_ALLOWANCES]: "Claiming No Personal Allowances",
  [StateTaxFilingStatus.CLAIMING_SELF]: "Claiming Self",

  [StateTaxFilingStatus.D]: "Chart D",
  [StateTaxFilingStatus.E]: "Chart E",
  [StateTaxFilingStatus.F]: "Chart F",

  [StateTaxFilingStatus.HEAD_OF_HOUSEHOLD]: "Head of Household",
  [StateTaxFilingStatus.HEAD_OF_HOUSEHOLD__HIGHER_RATES]: "Head of Household (Higher Rates)",
  [StateTaxFilingStatus.HEAD_OF_HOUSEHOLD_COMPLETE]: "Head of Household",
  [StateTaxFilingStatus.HEAD_OF_HOUSEHOLD_NONE]: "Head of Household (No Allowances)",

  [StateTaxFilingStatus.INDIVIDUAL_TAXPAYER_COMPLETE]: "Individual Taxpayer",
  [StateTaxFilingStatus.INDIVIDUAL_TAXPAYER_NONE]: "Individual Taxpayer (No Allowances)",

  [StateTaxFilingStatus.MARRIED]: "Married",
  [StateTaxFilingStatus.MARRIED__SURV_SPOUSE__HOH]: "Married / Surviving Spouse / Head of Household",

  [StateTaxFilingStatus.MARRIED_CIVIL_UNION_FILING_JOINTLY]: "Married or Civil Union Filing Jointly",
  [StateTaxFilingStatus.MARRIED_CIVIL_UNION_FILING_SEPARATELY]: "Married or Civil Union Filing Separately",

  [StateTaxFilingStatus.MARRIED_CLAIMING_BOTH]: "Married Claiming Both",
  [StateTaxFilingStatus.MARRIED_CLAIMING_GREATER_OR_EQUAL_TO_2]: "Married Claiming Two or More",
  [StateTaxFilingStatus.MARRIED_CLAIMING_LESS_THAN_2]: "Married Claiming Less Than Two",

  [StateTaxFilingStatus.MARRIED_COMPLETE]: "Married",

  [StateTaxFilingStatus.MARRIED_FILING_JOINTLY]: "Married Filing Jointly",
  [StateTaxFilingStatus.MARRIED_FILING_JOINTLY__HIGHER_RATES]: "Married Filing Jointly (Higher Rates)",
  [StateTaxFilingStatus.MARRIED_FILING_SEPARATE_ON_SAME_RETURN]: "Married Filing Separately (Same Return)",
  [StateTaxFilingStatus.MARRIED_FILING_SEPARATELY]: "Married Filing Separately",

  [StateTaxFilingStatus.MARRIED_FLNG_JOINT_CLAIM_HALF_EXEMPTS]: "Married Filing Jointly – Claim Half Exemptions",
  [StateTaxFilingStatus.MARRIED_LVNG_W_SPOUSE_FLNG_SEP_CLAIM_ALL]: "Married Living With Spouse – Filing Separately Claim All",

  [StateTaxFilingStatus.MARRIED_NONE_OR_MARRIED_OPT_COMP_NONE]: "Married – Optional Computation (No Allowances)",
  [StateTaxFilingStatus.MARRIED_NOT_LIVING_WITH_SPOUSE]: "Married – Not Living With Spouse",
  [StateTaxFilingStatus.MARRIED_OPTIONAL_COMPUTATION_COMPLETE]: "Married – Optional Computation",
  [StateTaxFilingStatus.MARRIED_PERSON_HALF]: "Married – Claim Half",

  [StateTaxFilingStatus.MARRIED_SPOUSE_NOT_WORKING]: "Married – Spouse Not Working",

  [StateTaxFilingStatus.MARRIED_WITH_MULTIPLE_EMPLOYERS]: "Married with Multiple Employers",
  [StateTaxFilingStatus.MARRIED_WITH_ONE_INCOME]: "Married – One Income",
  [StateTaxFilingStatus.MARRIED_WITH_SPOUSE_FILING]: "Married – Spouse Filing",
  [StateTaxFilingStatus.MARRIED_WITH_SPOUSE_WORKING]: "Married – Spouse Working",
  [StateTaxFilingStatus.MARRIED_WITH_TWO_INCOMES]: "Married with Two Incomes",
  [StateTaxFilingStatus.MARRIED_WITHHOLD_SINGLE]: "Married – Withhold at Single Rate",
  [StateTaxFilingStatus.MARRIED_WITHOUT_SPOUSE_FILING]: "Married – Without Spouse Filing",

  [StateTaxFilingStatus.MFS_OR_MFJ__BOTH_SPOUSES_WORKING]: "Married Filing Jointly or Separately – Both Spouses Working",
  [StateTaxFilingStatus.MJQH]: "Married Joint / Head of Household",

  [StateTaxFilingStatus.NO_DEDUCTION]: "No Deduction",
  [StateTaxFilingStatus.OTHER]: "Other",

  [StateTaxFilingStatus.QUALIFYING_WIDOWER]: "Qualifying Surviving Spouse",
  [StateTaxFilingStatus.REGULAR]: "Regular",

  [StateTaxFilingStatus.SINGLE]: "Single",
  [StateTaxFilingStatus.SINGLE_MARRIED_CIVIL_UNION_SEP]: "Single / Married / Civil Union (Separate)",
  [StateTaxFilingStatus.SINGLE_MARRIED_FILING_SEP__HIGHER_RATES]: "Single or Married Filing Separately (Higher Rates)",
  [StateTaxFilingStatus.SINGLE_MARRIED_WITH_TWO_OR_MORE_INCOMES]: "Single or Married with Two or More Incomes",
  [StateTaxFilingStatus.SINGLE_OR_MARRIED_FILING_SEPARATELY]: "Single or Married Filing Separately",
};

export const TaxStatusType = Object.freeze({
  EXEMPT: "EXEMPT",
  EXEMPT_FROM_WITHHOLDING: "EXEMPT_FROM_WITHHOLDING",
  NO_REPORTING_OR_WITHHOLDING_REQUIRED: "NO_REPORTING_OR_WITHHOLDING_REQUIRED",
  PARTIALLY_TAXABLE: "PARTIALLY_TAXABLE",
  REPORTABLE_BUT_NO_WITHHOLDING: "REPORTABLE_BUT_NO_WITHHOLDING",
  TAXABLE: "TAXABLE",
  WITHHOLD: "WITHHOLD",
});

export const TaxStatusTypeLabels = {
  [TaxStatusType.EXEMPT]: "Exempt",
  [TaxStatusType.EXEMPT_FROM_WITHHOLDING]: "Exempt from Withholding",
  [TaxStatusType.NO_REPORTING_OR_WITHHOLDING_REQUIRED]: "No Reporting or Withholding Required",
  [TaxStatusType.PARTIALLY_TAXABLE]: "Partially Taxable",
  [TaxStatusType.REPORTABLE_BUT_NO_WITHHOLDING]: "Reportable but No Withholding",
  [TaxStatusType.TAXABLE]: "Taxable",
  [TaxStatusType.WITHHOLD]: "Withhold",
};

const StatusIcon = ({ icon, tooltip, className }) => (
  <OverlayTrigger placement="left" overlay={tooltip}>
    <span className={`ms-2 ${className}`} style={{ cursor: "help", lineHeight: 1 }}>
      <FontAwesomeIcon icon={icon} size="sm" />
    </span>
  </OverlayTrigger>
);

const PaychexDependentSwitch = ({ value, setValue, hasSupportingDocument, documentType, documentRequest, optionsDisabled }) => {
  const CONFIG = {
    i9: {
      warning: "It is not recommended to send Paychex worker data without a completed I-9 document.",
      sentMessage: "This user has already had personal data sent to Paychex from this document request.",
      sentFlag: documentRequest?.createdPaychexWorker,
    },
    w4: {
      warning: "It is not recommended to send federal tax data without a completed W-4 form.",
      sentMessage: "This user has already had federal tax data sent to Paychex from this document request.",
      sentFlag: documentRequest?.submittedFederalTaxToPaychex,
    },
    stateTax: {
      warning: "It is not recommended to send state tax data without a completed state tax form.",
      sentMessage: "This user has already had state tax data sent to Paychex from this document request.",
      sentFlag: documentRequest?.submittedStateTaxToPaychex,
    },
    bank: {
      warning: "It is not recommended to send direct deposit data without a completed bank authorization form.",
      sentMessage: "This user has already had direct deposit data sent to Paychex from this document request.",
      sentFlag: documentRequest?.submittedDdToPaychex,
    },
  };

  const config = CONFIG[documentType] ?? {};

  const isDisabled = documentType !== "i9" && optionsDisabled;
  const hasBeenSent = !!config.sentFlag;
  const showWarning = !isDisabled && !hasSupportingDocument && !!documentType && !hasBeenSent;

  const tooltips = {
    disabled: (
      <Tooltip>
        This user is not associated with a Paychex Worker ID.
        <br />
        <strong>Paychex Worker ID</strong> must be enabled to use this option.
      </Tooltip>
    ),

    warning: (
      <Tooltip>
        <strong>Warning</strong>
        <br />
        {config.warning}
        <br />
        All information should be reviewed and verified in Paychex.
      </Tooltip>
    ),

    sent: (
      <Tooltip>
        <strong>Note</strong>
        <br />
        {config.sentMessage}
      </Tooltip>
    ),
  };

  const icons = [];

  if (isDisabled) {
    icons.push(<StatusIcon key="disabled" icon={faTriangleExclamation} tooltip={tooltips.disabled} className="text-danger" />);
  } else if (showWarning) {
    icons.push(<StatusIcon key="warning" icon={faTriangleExclamation} tooltip={tooltips.warning} className="text-warning" />);
  }

  if (hasBeenSent) {
    icons.push(<StatusIcon key="sent" icon={faCheck} tooltip={tooltips.sent} className="text-success" />);
  }

  const title = (
    <span className="d-inline-flex align-items-center">
      Send Data
      {icons}
    </span>
  );

  return <SwitchControl title={title} value={value} setValue={setValue} disabled={isDisabled} />;
};

export default function PaychexInProgressWorkerModal({ documentRequestId, hideModal }) {
  /// CONSTANTS
  const STATES_DOCUMENT_SUPPORTED = [
    { value: "AL", label: "Alabama" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "IN", label: "Indiana" },
    { value: "IL", label: "Illinois" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "MI", label: "Michigan" },
    { value: "MO", label: "Missouri" },
    { value: "MS", label: "Mississippi" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NY", label: "New York" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "PA", label: "Pennsylvania" },
    { value: "VA", label: "Virginia" },
    { value: "WI", label: "Wisconsin" },
  ];

  const DOCUMENT_TYPE_TO_STATE = {
    de4: "CA",
    dr0004: "CO",
    f93850: "MS",
    g4: "GA",
    a4: "AL",
    wh4: "IN",
    ilw4: "IL",
    k4: "KY",
    ks4: "KS",
    l4: "LA",
    miw4: "MI",
    mow4: "MO",
    mw4: "MT",
    rev419: "PA",
    it2104: "NY",
    it4: "OH",
    okw4: "OK",
    va4: "VA",
    w4n: "NE",
    wt4: "WI",
  };

  /// STATE VARIABLES
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documentRequest, setDocumentRequest] = useState();
  const [paychexWorkerId, setPaychexWorkerId] = useState(null);
  const [pdfBase64s, setPdfBase64s] = useState([]);
  const [personalData, setPersonalData] = useState({
    firstName: "",
    middleInitial: "",
    lastName: "",
    ssn: "",
    dateOfBirth: "",
    phoneNumber: "",
    email: "",
    hireDate: "",
    providedSsn: false,
    address: {},
  });
  const [federalTaxData, setFederalTaxData] = useState({
    filingStatus: "",
    multipleJobs: "",
    dependentsAmount: "",
    otherIncome: "",
    deductionsAmount: "",
    taxesWithheld: "",
    extraWithholdingAmount: "",
  });
  const [stateTaxData, setStateTaxData] = useState({
    countrySubdivisionCode: "",
    stateAllocationPercent: "",
    isResidentState: "",
    taxStatusType: null,
    filingStatusType: null,
    additionalAmount: "",
  });
  const [directDepositData, setDirectDepositData] = useState([
    {
      paymentType: "",
      accountType: "",
      value: "",
      routingNumber: "",
      accountNumber: "",
      enabled: true,
    },
    {
      paymentType: "",
      accountType: "",
      value: "",
      routingNumber: "",
      accountNumber: "",
      enabled: false,
    },
  ]);

  const [personalDataEnabled, setPersonalDataEnabled] = useState(false);
  const [federalTaxDataEnabled, setFederalTaxDataEnabled] = useState(false);
  const [stateTaxDataEnabled, setStateTaxDataEnabled] = useState(false);
  const [directDepositDataEnabled, setDirectDepositDataEnabled] = useState(false);

  const [federalTaxDataError, setFederalTaxDataError] = useState(null);
  const [stateTaxDataError, setStateTaxDataError] = useState(null);
  const [directDepositDataError, setDirectDepositDataError] = useState(null);

  const optionsDisabled = useMemo(() => {
    return !paychexWorkerId && !personalDataEnabled;
  }, [paychexWorkerId, personalDataEnabled]);

  const stateCode = useMemo(() => {
    return getStateTaxStateFromDocuments(documentRequest?.documents ?? {});
  }, [documentRequest]);

  const hasI9DocumentAndCompleted = useMemo(() => {
    const i9Document = documentRequest?.documents?.["i9"];
    return !!i9Document && i9Document.employerCompleted;
  }, [documentRequest]);

  const hasW4DocumentAndCompleted = useMemo(() => {
    const w4Document = documentRequest?.documents?.["w4"];
    return !!w4Document && w4Document.employerCompleted;
  }, [documentRequest]);

  const hasStateTaxDocumentAndCompleted = useMemo(() => {
    if (!stateCode) {
      return false;
    }

    const doc = documentRequest?.documents?.[stateCode];
    if (!doc) {
      return false;
    }

    if (!doc.employeeCompleted) {
      return false;
    }

    if ("employerCompleted" in doc && !doc.employerCompleted) {
      return false;
    }

    return true;
  }, [stateCode, documentRequest]);

  const hasBankDocumentAndCompleted = useMemo(() => {
    const dd = documentRequest?.documents?.["dd"];
    const mdd = documentRequest?.documents?.["mdd"];

    return Boolean((dd && dd.employeeCompleted) || (mdd && mdd.employeeCompleted));
  }, [documentRequest]);

  ///////////////////
  /// FUNCTIONS
  ///////////////////

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setIsLoading(true);
    const response = await getPaychexWorkerDocumentRequestData(documentRequestId);
    if (response.status == 200) {
      const documentRequest = response.documentRequest;
      const documents = documentRequest.documents;
      const paychexWorkerId = response.paychexWorkerId;

      const allowOptionsDefaultTrue = paychexWorkerId || documents["i9"]?.employeeCompleted;

      const stateCode = getStateTaxStateFromDocuments(documentRequest?.documents ?? {});
      const hasStateDocument = !!stateCode;

      /// ---------- Personal Info ----------
      const i9Document = documents?.["i9"];
      if (i9Document && i9Document.employeeCompleted) {
        const streetAddress = i9Document["streetAddress"];
        const aptNumber = i9Document["aptNumber"];
        const city = i9Document["city"];
        const state = i9Document["state"];
        const zipCode = i9Document["zipCode"];

        const address = {
          uid: -1,
          thoroughfare: streetAddress,
          premise: aptNumber,
          locality: city,
          administrativeArea: state,
          postalCode: zipCode,
        };

        setPersonalData({
          firstName: i9Document["firstName"],
          middleInitial: i9Document["middleInitial"],
          lastName: i9Document["lastName"],
          ssn: i9Document["ssn"],
          dateOfBirth: i9Document["dateOfBirth"],
          phoneNumber: i9Document["phoneNumber"],
          email: i9Document["emailAddress"],
          address: address,
          providedSsn: false,
          hireDate: i9Document["firstDayOfEmployment"],
        });

        if (!documentRequest["createdPaychexWorker"] && !paychexWorkerId) {
          setPersonalDataEnabled(true);
        }
      }

      const w4Document = documents?.["w4"];
      if (w4Document && w4Document.employeeCompleted) {
        /// --------- Federal Tax ------------

        setFederalTaxData({
          filingStatus: w4Document["filingStatus"] === 2 ? "HEAD_OF_HOUSEHOLD" : w4Document["filingStatus"] === 1 ? "MARRIED_FILING_JOINTLY" : "SINGLE_OR_MARRIED_FILING_SEPARATELY",
          multipleJobs: w4Document["has2Jobs"] === 1 ? "true" : "false",
          dependentsAmount: w4Document["step3Total"],
          otherIncome: w4Document["step4OtherIncome"],
          deductionsAmount: w4Document["step4Deductions"],
          taxesWithheld: w4Document["claimExemption"] === 1 ? "false" : "true",
          extraWithholdingAmount: w4Document["step4ExtraWithholding"],
        });

        if (!documentRequest["submittedFederalTaxToPaychex"] && allowOptionsDefaultTrue) {
          setFederalTaxDataEnabled(true);
        }
      }

      /// --------- State Tax ------------

      if (hasStateDocument) {
        const stateDocument = documents[Object.entries(DOCUMENT_TYPE_TO_STATE).find(([docType, state]) => state === stateCode)?.[0]];
        if (stateDocument.employeeCompleted) {
          setStateTaxData(buildInitialStateTaxData(stateCode, stateDocument));

          if (!documentRequest["submittedStateTaxToPaychex"] && allowOptionsDefaultTrue) {
            setStateTaxDataEnabled(true);
          }
        }
      }

      const ddDocument = documents?.["dd"];
      const mddDocument = documents?.["mdd"];

      /// ------ Direct Deposit ---------
      if (ddDocument || mddDocument) {
        if (ddDocument && ddDocument.employeeCompleted) {
          setDirectDepositData([
            {
              paymentType: "PERCENTAGE",
              accountType: ddDocument["accountType"] === 0 ? "CHECKING" : "SAVINGS",
              value: "100",
              routingNumber: ddDocument["routingNumber"],
              accountNumber: ddDocument["accountNumber"],
              enabled: true,
            },
            {
              paymentType: "PERCENTAGE",
              accountType: ddDocument["accountType"] === 0 ? "CHECKING" : "SAVINGS",
              value: "0",
              routingNumber: ddDocument["routingNumber"],
              accountNumber: ddDocument["accountNumber"],
              enabled: false,
            },
          ]);
        }

        if (mddDocument && mddDocument.employeeCompleted) {
          setDirectDepositData([
            {
              paymentType: mddDocument["depositType"] === 0 ? "FLAT_DOLLAR_AMOUNT" : "PERCENTAGE",
              accountType: mddDocument["accountType"] === 0 ? "CHECKING" : "SAVINGS",
              value: mddDocument["depositAmount1"],
              routingNumber: mddDocument["routingNumber1"],
              accountNumber: mddDocument["accountNumber1"],
              enabled: true,
            },
            {
              paymentType: mddDocument["depositType"] === 0 ? "FLAT_DOLLAR_AMOUNT" : "PERCENTAGE",
              accountType: mddDocument["accountType"] === 0 ? "CHECKING" : "SAVINGS",
              value: mddDocument["depositAmount2"],
              routingNumber: mddDocument["routingNumber1"],
              accountNumber: mddDocument["accountNumber1"],
              enabled: true,
            },
          ]);
        }

        if (!documentRequest["submittedDdToPaychex"] && allowOptionsDefaultTrue) {
          setDirectDepositDataEnabled(true);
        }
      }
      setPdfBase64s(response.base64s || []);
      setPaychexWorkerId(paychexWorkerId);
      setDocumentRequest(documentRequest);
    }
    setIsLoading(false);
  }

  async function submitPaychexData() {
    setIsSubmitting(true);
    setFederalTaxDataError(null);
    setStateTaxDataError(null);
    setDirectDepositDataError(null);

    const encodedData = {
      paychexWorkerId: paychexWorkerId,
      documentRequestId: documentRequestId,
    };

    const canSendDependentPayloads = !!paychexWorkerId || personalDataEnabled;

    if (personalDataEnabled) {
      encodedData["personalDataPayload"] = personalData;
    }
    if (federalTaxDataEnabled && canSendDependentPayloads) {
      encodedData["federalTaxPayload"] = federalTaxData;
    }
    if (stateTaxDataEnabled && canSendDependentPayloads) {
      encodedData["stateTaxPayload"] = stateTaxData;
    }
    if (directDepositDataEnabled && canSendDependentPayloads) {
      encodedData["directDepositPayload"] = directDepositData
        .filter((dd) => dd.enabled)
        .map((dd, index) => {
          const priority = index === 0 || dd.paymentType === "PERCENTAGE" ? index + 1 : 99;
          return { ...dd, priority: priority };
        });
    }

    const response = await submitPaychexWorkerDocumentRequestData(encodedData);
    if (response.status == 200) {
      const errors = response.errors ?? {};

      const federal = errors?.federalTaxDataError;
      const state = errors?.stateTaxDataError;
      const directDeposit = errors?.directDepositDataError;

      if (federal || state || directDeposit) {
        setFederalTaxDataError(federal ?? null);
        setStateTaxDataError(state ?? null);
        setDirectDepositDataError(directDeposit ?? null);
        AppGlobals.alert("danger", response.message);
      } else {
        AppGlobals.alert("success", response.message);
        hideModal();
      }
    } else {
      AppGlobals.alert("danger", response.message);
    }

    setIsSubmitting(false);
  }

  ///////////////////
  /// Helpers
  ///////////////////

  const makeUpdater = (setState) => (key, value) => {
    setState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const makeArrayUpdater = (setState) => (index, key, value) => {
    setState((prev) => prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  };

  const handleUpdatePersonalData = makeUpdater(setPersonalData);
  const handleUpdateFederalTaxData = makeUpdater(setFederalTaxData);
  const handleUpdateStateTaxData = makeUpdater(setStateTaxData);
  const handleUpdateDirectDepositData = makeArrayUpdater(setDirectDepositData);

  /// ---------- HELPERS FOR STATE TAX ----------------

  function getDocumentRequestDocumentStateCode(documentType) {
    return DOCUMENT_TYPE_TO_STATE[documentType] ?? null;
  }

  function getStateTaxStateFromDocuments(documents) {
    for (const documentType of Object.keys(documents)) {
      const stateCode = getDocumentRequestDocumentStateCode(documentType);
      if (stateCode) {
        return stateCode;
      }
    }

    return null;
  }

  function buildInitialStateTaxData(stateCode, stateDocument) {
    return {
      countrySubdivisionCode: stateCode,
      stateAllocationPercent: "100", // all others will be forced to 0 on back end
      isResidentState: "true", // force to true, might be better to check via address in i9, or system if i9 isnt attached
      taxStatusType: getTaxStatusType(stateCode, stateDocument),
      filingStatusType: getFilingStatusType(stateCode, stateDocument),
      additionalAmount: getAdditionalAmount(stateCode, stateDocument),
    };
  }

  function getAdditionalAmount(stateCode, stateDocument) {
    switch (stateCode) {
      case "AL":
      case "CO":
      case "KY":
      case "KS":
      case "NE":
      case "WI":
      case "MI":
      // case "IL": // Should support but paychex broken
      case "OK":
      case "MO":
        return validateDecimal(stateDocument["additionalWithholding"]).toString();
      case "CA":
        return validateDecimal(stateDocument["additionalAmount"]).toString();
      case "OH":
        return validateDecimal(stateDocument["section2AdditionalWithholdings"]).toString();
      case "LA":
        return validateDecimal(stateDocument["adjustments"]).toString();
      case "MT":
        return validateDecimal(stateDocument["extraWithholding"]).toString();
      case "IN":
        return validateDecimal(stateDocument["additionalStateWithholding"]).toString();
      case "VA":
        return validateDecimal(stateDocument["additionalWithholdings"]).toString();
      default:
        return null;
    }
  }

  function getFilingStatusType(stateCode, stateDocument) {
    switch (stateCode) {
      case "CA":
        if (stateDocument["filingStatus"] === 0) {
          return StateTaxFilingStatus.SINGLE_MARRIED_WITH_TWO_OR_MORE_INCOMES;
        }
        if (stateDocument["filingStatus"] === 1) {
          return StateTaxFilingStatus.MARRIED_WITH_ONE_INCOME;
        }
        if (stateDocument["filingStatus"] === 2) {
          return StateTaxFilingStatus.HEAD_OF_HOUSEHOLD;
        }
        return null;
      case "MT":
        if (stateDocument["marriedFilingJointly"]) {
          return StateTaxFilingStatus.MARRIED_FILING_JOINTLY;
        }

        if (stateDocument["filingStatus"] === 1) {
          return StateTaxFilingStatus.SINGLE_OR_MARRIED_FILING_SEPARATELY;
        }
        if (stateDocument["filingStatus"] === 2) {
          return StateTaxFilingStatus.MARRIED_FILING_JOINTLY;
        }
        if (stateDocument["filingStatus"] === 3) {
          return StateTaxFilingStatus.HEAD_OF_HOUSEHOLD;
        }
        return null;
      case "KS":
        if (stateDocument["filingStatus"] === 0) {
          return StateTaxFilingStatus.SINGLE;
        }
        if (stateDocument["filingStatus"] === 1) {
          return StateTaxFilingStatus.MARRIED_FILING_JOINTLY;
        }
        return null;
      case "LA":
        if (stateDocument["filingStatus"] === 0) {
          return StateTaxFilingStatus.NO_DEDUCTION;
        }
        if (stateDocument["filingStatus"] === 1) {
          return StateTaxFilingStatus.SINGLE_OR_MARRIED_FILING_SEPARATELY;
        }
        if (stateDocument["filingStatus"] === 2) {
          return StateTaxFilingStatus.MARRIED_FILING_JOINTLY;
        }
        return null;
      case "MS":
        if (stateDocument["singleExemption"]) {
          return StateTaxFilingStatus.SINGLE;
        }
        if (stateDocument["married"] === 0) {
          return StateTaxFilingStatus.MARRIED_SPOUSE_NOT_WORKING;
        }
        if (stateDocument["married"] === 1) {
          return StateTaxFilingStatus.MARRIED_WITH_SPOUSE_WORKING;
        }
        if (stateDocument["headOfFamily"]) {
          return StateTaxFilingStatus.HEAD_OF_HOUSEHOLD;
        }
        return null;
      case "NE":
        if (stateDocument["singleOrMarried"] === 0) {
          return StateTaxFilingStatus.SINGLE;
        }
        if (stateDocument["singleOrMarried"] === 1) {
          return StateTaxFilingStatus.MARRIED_FILING_JOINTLY;
        }
      case "MO":
        if (stateDocument["filingStatus"] === 0) {
          return StateTaxFilingStatus.SINGLE;
        }
        if (stateDocument["filingStatus"] === 1) {
          return StateTaxFilingStatus.MARRIED_SPOUSE_NOT_WORKING;
        }
        if (stateDocument["filingStatus"] === 2) {
          return StateTaxFilingStatus.HEAD_OF_HOUSEHOLD;
        }
        return null;
      case "WI":
        if (stateDocument["filingStatus"] === 1) {
          return StateTaxFilingStatus.SINGLE;
        }
        if (stateDocument["filingStatus"] === 2) {
          return StateTaxFilingStatus.MARRIED;
        }
        if (stateDocument["filingStatus"] === 3) {
          return StateTaxFilingStatus.MARRIED_WITHHOLD_SINGLE;
        }
        return null;
      case "OK":
        if (stateDocument["filingStatus"] === 0) {
          return StateTaxFilingStatus.SINGLE;
        }
        if (stateDocument["filingStatus"] === 1) {
          return StateTaxFilingStatus.MARRIED;
        }
        if (stateDocument["filingStatus"] === 2) {
          return StateTaxFilingStatus.MARRIED_WITHHOLD_SINGLE;
        }
        return null;
      case "MI":
      case "PA":
      case "OH":
      case "KY":
      case "IN":
      case "CO": // Does have a filing status type, but unable to discern with data stored in our system, let paychex give default

      // NOT SUPPORTED
      case "IL":
      case "ND":
      case "SD":
      case "MN":
      case "NE":
      case "WY":
      case "WA":
        return null;
      default:
        return null;
    }
  }

  function getTaxStatusType(stateCode, stateDocument) {
    switch (stateCode) {
      case "CA":
        // These seem like they might be reversed but should be correct
        if (stateDocument["claimExemption"]) {
          return TaxStatusType.EXEMPT_FROM_WITHHOLDING;
        }
        if (stateDocument["notSubjectToWithholding"]) {
          return TaxStatusType.EXEMPT;
        }
        return TaxStatusType.WITHHOLD;
      case "MT":
        if (stateDocument["reasonForExemption"] !== null && stateDocument["reasonForExemption"] > -1) {
          return TaxStatusType.EXEMPT;
        }
        return TaxStatusType.WITHHOLD;
      case "KS":
        if (stateDocument["claimExempt"]) {
          return TaxStatusType.EXEMPT_FROM_WITHHOLDING;
        }
        return TaxStatusType.WITHHOLD;
      case "MS":
        if (stateDocument["militarySpouseResidencyExemption"]) {
          return TaxStatusType.EXEMPT;
        }
        return TaxStatusType.WITHHOLD;
      case "MI":
        if (stateDocument["reasonForExemption"] !== null) {
          if ([0, 2].includes(stateDocument["reasonForExemption"])) {
            return TaxStatusType.EXEMPT;
          }
          if (stateDocument["reasonForExemption"] === 1) {
            return TaxStatusType.EXEMPT_FROM_WITHHOLDING;
          }
          return TaxStatusType.WITHHOLD;
        }
      case "PA":
        if (stateDocument["section2a"] || stateDocument["section2b"] || stateDocument["section2c"]) {
          return TaxStatusType.EXEMPT_FROM_WITHHOLDING;
        }
        return TaxStatusType.WITHHOLD;
      case "WI":
        if (stateDocument["claimExempt"]) {
          return TaxStatusType.EXEMPT_FROM_WITHHOLDING;
        }
        return TaxStatusType.WITHHOLD;
      case "OH":
        if (
          stateDocument["section3FullYearResident"] ||
          stateDocument["section3ResidentMilitary"] ||
          stateDocument["section3NonResidentMilitary"] ||
          stateDocument["section3NonResidentCivilian"] ||
          stateDocument["section3Exempt"]
        ) {
          return TaxStatusType.EXEMPT_FROM_WITHHOLDING;
        }
        return TaxStatusType.WITHHOLD;
      case "KY":
        if (stateDocument["exemptBox1"] || stateDocument["exemptBox2"] || stateDocument["exemptBox3"] || stateDocument["exemptBox4"]) {
          return TaxStatusType.EXEMPT;
        }
        return TaxStatusType.WITHHOLD;
      case "NE":
        if (stateDocument["isExempt"]) {
          return TaxStatusType.EXEMPT_FROM_WITHHOLDING;
        }
        return TaxStatusType.WITHHOLD;
      case "IL":
        if (stateDocument["exempt"]) {
          return TaxStatusType.EXEMPT_FROM_WITHHOLDING;
        }
        return TaxStatusType.WITHHOLD;
      case "MO":
        if (stateDocument["exempt1"] || stateDocument["exempt2"] || stateDocument["exempt3"]) {
          return TaxStatusType.EXEMPT;
        }
        return TaxStatusType.WITHHOLD;
      case "OK":
        if (stateDocument["exemptStatus"] || stateDocument["civilServiceExempt"] || stateDocument["armedForceExempt"]) {
          return TaxStatusType.EXEMPT;
        }
        return TaxStatusType.WITHHOLD;
      case "LA": // Believe that exemption is tied to different unsupported L-4E form
      case "CO": // Not applicable?
      case "IN": // Not applicable?

      // NOT SUPPORTED
      case "MO":
      case "ND":
      case "SD":
      case "MN":
      case "NE":
      case "WY":
      case "WA":
        return null;
      default:
        return null;
    }
  }

  ///////////////////
  /// Render
  ///////////////////

  return isLoading ? (
    <Modal.Body className="d-flex justify-content-center align-items-center">
      <CustomSpinner />
    </Modal.Body>
  ) : (
    <>
      <Modal.Header className="py-2" closeButton>
        <div className="d-flex align-items-center gap-2">
          <Modal.Title className="mb-0">Paychex Worker</Modal.Title>

          <OverlayTrigger placement="right" overlay={<Tooltip id="paychex-worker-beta-tooltip">This feature is in beta. Some functionality may be incomplete or behave unexpectedly.</Tooltip>}>
            <Badge bg="warning" text="dark" pill style={{ cursor: "help" }}>
              Beta
            </Badge>
          </OverlayTrigger>
        </div>
      </Modal.Header>
      <Modal.Header className="small py-2">
        <div className="d-flex flex-column small">
          <span className="text-muted small">
            This process submits employee data to Paychex from the document request. It may create a new in-progress worker and add or update federal tax, state tax, and direct deposit records based
            on the information provided.
            <br />
            <strong className="text-warning">
              Information must be manually verified in Paychex. This feature is intended to assist with data entry only and is not a replacement for completing or validating employee setup directly
              within Paychex.
            </strong>
          </span>
        </div>
      </Modal.Header>

      <Modal.Body>
        <Container style={{ maxWidth: "100%" }}>
          <Row>
            <Col md={12} lg={6}>
              <Card style={{ maxHeight: "100%", overflowY: "auto" }}>
                {pdfBase64s.length === 0 ? (
                  <div
                    style={{
                      height: "100%",
                      minHeight: 300,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 24,
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    No Relevant Completed Documents
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {pdfBase64s.map((b64, idx) => {
                      const isPdf = b64.startsWith("data:application/pdf");
                      const isImage = b64.startsWith("data:image");

                      return (
                        <div key={idx} style={{ width: "100%" }}>
                          {isPdf && <iframe src={b64} style={{ width: "100%", height: 1000, border: "none" }} title={`Document ${idx + 1}`} />}

                          {isImage && (
                            <img
                              src={b64}
                              alt={`Document ${idx + 1}`}
                              style={{
                                width: "100%",
                                height: "auto",
                                borderRadius: 8,
                                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Card>
                  <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                      <Card.Title className="mb-0">Paychex Worker ID</Card.Title>

                      <PaychexDependentSwitch
                        value={personalDataEnabled}
                        setValue={(value) => {
                          if (!paychexWorkerId && !value) {
                            setFederalTaxDataEnabled(false);
                            setStateTaxDataEnabled(false);
                            setDirectDepositDataEnabled(false);
                          }
                          setPersonalDataEnabled(value);
                        }}
                        hasSupportingDocument={hasI9DocumentAndCompleted}
                        documentType={"i9"}
                        documentRequest={documentRequest}
                        optionsDisabled={optionsDisabled}
                      />
                    </div>

                    <span className="text-muted small mt-2">
                      Sends personal and employment information to Paychex. Fields are prefilled from the employee’s I-9 when available. If the worker does not yet exist in Paychex, this will create a
                      new in-progress worker. Existing workers will be updated as needed. This will also update user data in TTA systems.
                    </span>
                  </Card.Header>
                  {personalDataEnabled && (
                    <Card.Body>
                      <Row>
                        <CustomControl
                          xl={4}
                          max={30}
                          title="First Name"
                          value={personalData.firstName}
                          onChange={(event) => {
                            handleUpdatePersonalData("firstName", event.target.value);
                          }}
                        />
                        <CustomControl
                          xl={4}
                          max={1}
                          title="Middle Initial"
                          value={personalData.middleInitial}
                          onChange={(event) => {
                            handleUpdatePersonalData("middleInitial", event.target.value);
                          }}
                        />
                        <CustomControl
                          xl={4}
                          max={30}
                          title="Last Name"
                          value={personalData.lastName}
                          onChange={(event) => {
                            handleUpdatePersonalData("lastName", event.target.value);
                          }}
                        />
                        <CustomControl
                          xl={4}
                          max={10}
                          title="SSN"
                          type="number"
                          value={personalData.ssn}
                          onChange={(event) => {
                            handleUpdatePersonalData("ssn", event.target.value);
                          }}
                        />
                        <SwitchControl
                          xl={4}
                          style={{ padding: "14px 12px" }}
                          title={"Provided SSN Card"}
                          value={personalData.providedSsn}
                          setValue={(value) => handleUpdatePersonalData("providedSsn", value)}
                        />
                        <CustomControl
                          xl={4}
                          max={10}
                          title="Date of Birth"
                          type="date"
                          value={personalData.dateOfBirth}
                          onChange={(event) => {
                            handleUpdatePersonalData("dateOfBirth", event.target.value);
                          }}
                        />
                        <CustomControl
                          xl={4}
                          max={10}
                          title="Phone Number"
                          type="number"
                          value={personalData.phoneNumber}
                          onChange={(event) => {
                            handleUpdatePersonalData("phoneNumber", event.target.value);
                          }}
                        />
                        <CustomControl
                          xl={4}
                          max={50}
                          title="Email"
                          value={personalData.email}
                          onChange={(event) => {
                            handleUpdatePersonalData("email", event.target.value);
                          }}
                        />
                        <CustomControl
                          xl={4}
                          title="Hire Date"
                          type={"date"}
                          value={personalData.hireDate}
                          onChange={(event) => {
                            handleUpdatePersonalData("hireDate", event.target.value);
                          }}
                        />

                        <AddressControl
                          xl={12}
                          title="Address"
                          address={personalData.address}
                          setAddress={(value) => {
                            handleUpdatePersonalData("address", value);
                          }}
                        />
                      </Row>
                    </Card.Body>
                  )}
                </Card>

                <Card>
                  <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                      <Card.Title className="mb-0">Federal Tax</Card.Title>

                      <PaychexDependentSwitch
                        value={federalTaxDataEnabled}
                        setValue={setFederalTaxDataEnabled}
                        hasSupportingDocument={hasW4DocumentAndCompleted}
                        documentType={"w4"}
                        documentRequest={documentRequest}
                        optionsDisabled={optionsDisabled}
                      />
                    </div>

                    <span className="text-muted small mt-2">
                      Adds or updates federal tax information for this Paychex worker. Fields are prefilled from the employee’s W-4 when available. If no W-4 exists, the information can be entered
                      manually.
                    </span>
                  </Card.Header>
                  {federalTaxDataError && (
                    <Alert variant="danger" className="mx-3 mt-3 mb-0">
                      <strong>Federal Tax Issues</strong>
                      <p>{federalTaxDataError}</p>
                    </Alert>
                  )}
                  {federalTaxDataEnabled && (
                    <Card.Body>
                      <Row>
                        <RadioControl
                          title="Filing Status"
                          xl={12}
                          selection={federalTaxData.filingStatus}
                          setSelection={(value) => handleUpdateFederalTaxData("filingStatus", value)}
                          inline
                          optionNames={["Single or Married Filing Separately", "Married Filing Jointly", "Head of Household"]}
                          optionValues={["SINGLE_OR_MARRIED_FILING_SEPARATELY", "MARRIED_FILING_JOINTLY", "HEAD_OF_HOUSEHOLD"]}
                        />
                        <RadioControl
                          title="Multiple Jobs"
                          xl={6}
                          selection={federalTaxData.multipleJobs}
                          setSelection={(value) => handleUpdateFederalTaxData("multipleJobs", value)}
                          inline
                          optionNames={["No", "Yes"]}
                          optionValues={["false", "true"]}
                        />
                        <RadioControl
                          title="Taxes Withheld"
                          xl={6}
                          selection={federalTaxData.taxesWithheld}
                          setSelection={(value) => handleUpdateFederalTaxData("taxesWithheld", value)}
                          inline
                          optionNames={["No", "Yes"]}
                          optionValues={["false", "true"]}
                        />
                        <CustomControl
                          xl={4}
                          title="Dependents Amount"
                          type="number"
                          value={federalTaxData.dependentsAmount}
                          onChange={(event) => {
                            handleUpdateFederalTaxData("dependentsAmount", event.target.value);
                          }}
                        />
                        <CustomControl
                          xl={4}
                          title="Other Income"
                          type="number"
                          value={federalTaxData.otherIncome}
                          onChange={(event) => {
                            handleUpdateFederalTaxData("otherIncome", event.target.value);
                          }}
                        />
                        <CustomControl
                          xl={4}
                          title="Deductions Amount"
                          type="number"
                          value={federalTaxData.deductionsAmount}
                          onChange={(event) => {
                            handleUpdateFederalTaxData("deductionsAmount", event.target.value);
                          }}
                        />
                        <CustomControl
                          xl={4}
                          title="Extra Withholding Amount"
                          type="number"
                          value={federalTaxData.extraWithholdingAmount}
                          onChange={(event) => {
                            handleUpdateFederalTaxData("extraWithholdingAmount", event.target.value);
                          }}
                        />
                      </Row>
                    </Card.Body>
                  )}
                </Card>
                <Card>
                  <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                      <Card.Title className="mb-0">State Tax</Card.Title>

                      <PaychexDependentSwitch
                        value={stateTaxDataEnabled}
                        setValue={setStateTaxDataEnabled}
                        hasSupportingDocument={hasStateTaxDocumentAndCompleted}
                        documentType={"stateTax"}
                        documentRequest={documentRequest}
                        optionsDisabled={optionsDisabled}
                      />
                    </div>
                    <span className="text-muted small mt-2">
                      Adds or updates state tax information for this Paychex worker. Fields are prefilled from the employee’s state tax form when available. If no state tax form exists, the
                      information can be entered manually.
                    </span>
                  </Card.Header>
                  {stateTaxDataError && (
                    <Alert variant="danger" className="mx-3 mt-3 mb-0">
                      <strong>State Tax Issues</strong>
                      <p>{stateTaxDataError}</p>
                    </Alert>
                  )}
                  {stateTaxDataEnabled && (
                    <Card.Body>
                      <Row>
                        <DropdownControl
                          title="Select State"
                          selection={stateTaxData.countrySubdivisionCode}
                          setSelection={(value) => handleUpdateStateTaxData("countrySubdivisionCode", value)}
                          disabled={!!stateCode}
                          itemNames={STATES_DOCUMENT_SUPPORTED.map((s) => s.label)}
                          itemValues={STATES_DOCUMENT_SUPPORTED.map((s) => s.value)}
                        />
                        {stateTaxData.filingStatusType !== null && (
                          <RadioControl
                            title="Filing Status"
                            xl={12}
                            selection={stateTaxData.filingStatusType}
                            setSelection={(value) => handleUpdateStateTaxData("filingStatusType", value)}
                            inline
                            optionValues={Object.values(StateTaxFilingStatus)}
                            optionNames={Object.values(StateTaxFilingStatus).map((status) => StateTaxFilingStatusLabels[status])}
                          />
                        )}
                        {stateTaxData.taxStatusType !== null && (
                          <RadioControl
                            title="Tax Status"
                            xl={12}
                            selection={stateTaxData.taxStatusType}
                            setSelection={(value) => handleUpdateStateTaxData("taxStatusType", value)}
                            inline
                            optionValues={Object.values(TaxStatusType)}
                            optionNames={Object.values(TaxStatusType).map((status) => TaxStatusTypeLabels[status])}
                          />
                        )}
                        <RadioControl
                          title="Is Resident State?"
                          xl={12}
                          selection={stateTaxData.isResidentState}
                          setSelection={(value) => handleUpdateStateTaxData("isResidentState", value)}
                          inline
                          optionNames={["Yes", "No"]}
                          optionValues={["true", "false"]}
                        />

                        {stateTaxData.additionalAmount !== null && (
                          <CustomControl
                            xl={12}
                            title="Additional Amount"
                            value={stateTaxData.additionalAmount}
                            onChange={(event) => handleUpdateStateTaxData("additionalAmount", event.target.value)}
                          />
                        )}
                      </Row>
                      <Alert variant="info" className="mt-3">
                        <strong>Note:</strong> State taxes cannot be assumed to be complete or fully calculated using this feature alone.
                      </Alert>
                    </Card.Body>
                  )}
                </Card>

                <Card>
                  <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                      <Card.Title className="mb-0">Direct Deposit</Card.Title>

                      <PaychexDependentSwitch
                        value={directDepositDataEnabled}
                        setValue={setDirectDepositDataEnabled}
                        hasSupportingDocument={hasBankDocumentAndCompleted}
                        documentType={"bank"}
                        documentRequest={documentRequest}
                        optionsDisabled={optionsDisabled}
                      />
                    </div>

                    <span className="text-muted small mt-2">
                      Adds or updates direct deposit information for this Paychex worker. Fields are prefilled from the employee’s Direct Deposit (DD) or Modified Direct Deposit (MDD) form when
                      available. If no form exists, the information can be entered manually.
                    </span>
                  </Card.Header>
                  {directDepositDataError && (
                    <Alert variant="danger" className="mx-3 mt-3 mb-0">
                      <strong>Direct Deposit Issues</strong>
                      <p>{directDepositDataError}</p>
                    </Alert>
                  )}
                  {directDepositDataEnabled && (
                    <Card.Body>
                      <Row>
                        <span className="fw-semibold mb-2">Account 1</span>

                        <RadioControl
                          title="Deposit Type"
                          xl={6}
                          selection={directDepositData[0].paymentType}
                          setSelection={(value) => {
                            handleUpdateDirectDepositData(0, "paymentType", value);
                            if (value === "FLAT_DOLLAR_AMOUNT") {
                              handleUpdateDirectDepositData(1, "paymentType", "REMAINDER");
                            } else {
                              handleUpdateDirectDepositData(1, "paymentType", "PERCENTAGE");
                            }
                          }}
                          inline
                          optionNames={["Flat Dollar Amount", "Percentage"]}
                          optionValues={["FLAT_DOLLAR_AMOUNT", "PERCENTAGE"]}
                        />

                        <RadioControl
                          title="Account Type"
                          xl={6}
                          selection={directDepositData[0].accountType}
                          setSelection={(value) => handleUpdateDirectDepositData(0, "accountType", value)}
                          inline
                          optionNames={["Checking", "Savings"]}
                          optionValues={["CHECKING", "SAVINGS"]}
                        />

                        <CustomControl
                          xl={4}
                          title={directDepositData[0].paymentType === "PERCENTAGE" ? "Percentage" : "Amount"}
                          value={directDepositData[0].value}
                          type={"number"}
                          onChange={(event) => {
                            handleUpdateDirectDepositData(0, "value", event.target.value);
                            if (directDepositData[0].paymentType === "PERCENTAGE") {
                              const value = validateDecimal(event.target.value);
                              handleUpdateDirectDepositData(1, "value", 100 - value);
                            } else {
                              handleUpdateDirectDepositData(1, "value", 0);
                            }
                          }}
                        />

                        <CustomControl
                          xl={4}
                          title="Routing Number"
                          value={directDepositData[0].routingNumber}
                          onChange={(event) => handleUpdateDirectDepositData(0, "routingNumber", event.target.value)}
                        />

                        <CustomControl
                          xl={4}
                          title="Account Number"
                          value={directDepositData[0].accountNumber}
                          onChange={(event) => handleUpdateDirectDepositData(0, "accountNumber", event.target.value)}
                        />

                        <div className="mt-3">
                          <SwitchControl title="Enable Second Account" value={directDepositData[1].enabled} setValue={(enabled) => handleUpdateDirectDepositData(1, "enabled", enabled)} />
                        </div>

                        {directDepositData[1].enabled && (
                          <>
                            <span className="fw-semibold mt-3 mb-2">Account 2</span>

                            <RadioControl
                              title="Deposit Type"
                              xl={6}
                              selection={directDepositData[1].paymentType}
                              setSelection={(value) => handleUpdateDirectDepositData(1, "paymentType", value)}
                              inline
                              disabled
                              optionNames={["Remainder", "Percentage"]}
                              optionValues={["REMAINDER", "PERCENTAGE"]}
                            />
                            <RadioControl
                              title="Account Type"
                              xl={6}
                              selection={directDepositData[1].accountType}
                              setSelection={(value) => handleUpdateDirectDepositData(1, "accountType", value)}
                              inline
                              optionNames={["Checking", "Savings"]}
                              optionValues={["CHECKING", "SAVINGS"]}
                            />

                            <CustomControl
                              xl={4}
                              type={"number"}
                              title={directDepositData[1].paymentType === "PERCENTAGE" ? "Percentage" : "Amount"}
                              value={directDepositData[1].value}
                              disabled
                              onChange={(event) => handleUpdateDirectDepositData(1, "value", event.target.value)}
                            />

                            <CustomControl
                              xl={4}
                              title="Routing Number"
                              value={directDepositData[1].routingNumber}
                              onChange={(event) => handleUpdateDirectDepositData(1, "routingNumber", event.target.value)}
                            />

                            <CustomControl
                              xl={4}
                              title="Account Number"
                              value={directDepositData[1].accountNumber}
                              onChange={(event) => handleUpdateDirectDepositData(1, "accountNumber", event.target.value)}
                            />
                          </>
                        )}
                      </Row>
                    </Card.Body>
                  )}
                </Card>
              </div>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
      <Modal.Footer style={{ padding: 2 }}>
        <CustomButton label={"Submit"} isLoading={isSubmitting} onClick={() => submitPaychexData()} />
      </Modal.Footer>
    </>
  );
}
